import React, { useState, useEffect } from 'react';
import { useAlert } from '../components/AlertContext';
import { validarRut, formatearRut } from '../utils/validaciones';
import './Dashboard.css';

const CATEGORIAS = {
  Activo: [
    'Computadores', 'Servidores', 'Teléfonos', 'Impresoras Multifunción',
    'Tablets', 'Cámaras de Seguridad', 'Escáneres', 'Switches', 'Routers', 'Otro'
  ],
  Periferico: [
    'Monitor', 'Teclado', 'Mouse', 'Webcam USB', 'Parlantes', 'Cargador/Adaptador', 'Otro'
  ]
};

const SolicitudPublica = () => {
  const { toast } = useAlert();
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;

  const [etapa, setEtapa] = useState('RUT');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [equiposFuncionario, setEquiposFuncionario] = useState([]);
  const [funcionariosDirectorio, setFuncionariosDirectorio] = useState([]);
  const [categoriasDB, setCategoriasDB] = useState([]);
  const [theme, setTheme] = useState(localStorage.getItem('app-theme') || 'dark');

  useEffect(() => {
    fetch(`${apiBase}/api/publico/funcionarios`)
      .then(res => res.json())
      .then(data => setFuncionariosDirectorio(data))
      .catch(console.error);

    fetch(`${apiBase}/api/categorias`)
      .then(res => res.json())
      .then(data => setCategoriasDB(data))
      .catch(console.error);
  }, [apiBase]);

  const getCategoriasDisponibles = (tipo) => {
    const claseMapeada = tipo === 'Activo' ? 'Activo' : 'Periferico';
    const catsDB = categoriasDB.filter(c => c.clase === claseMapeada).map(c => c.nombre);
    const catsHardcodeadas = CATEGORIAS[tipo] || [];
    const combinadas = [...new Set([...catsHardcodeadas, ...catsDB])];
    return combinadas;
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('app-theme', newTheme);
    document.body.setAttribute('data-theme', newTheme);
  };

  const [funcionarioData, setFuncionarioData] = useState({
    rut: '',
    nombre: '',
    email: '',
    departamento: '',
    cargo: ''
  });

  const [solicitudData, setSolicitudData] = useState({
    tipo: 'Activo',
    categoria: 'Computadores',
    nombre_equipo: '',
    motivo: '',
    tipo_solicitud: 'Nuevo Equipo',
    equipo_id: '',
    destino_traslado: '',
    fecha_devolucion: '',
    destinatario_prestamo: ''
  });


  const handleVerifyRut = async (e) => {
    e.preventDefault();
    const rutIngresado = funcionarioData.rut;
    if (!rutIngresado) return;

    if (!validarRut(rutIngresado)) {
      toast.error('El RUT ingresado no es válido (Módulo 11). Por favor, revísalo.');
      return;
    }

    const rutFormateado = formatearRut(rutIngresado);
    setFuncionarioData({ ...funcionarioData, rut: rutFormateado });

    setIsSubmitting(true);
    try {
      const resp = await fetch(`${apiBase}/api/publico/verificar-funcionario/${rutFormateado}`, { credentials: 'include' });
      const data = await resp.json();
      if (data.existe) {
        if (data.pending) {
          toast.warning('Estimado/a, su registro aún está "Pendiente de Aprobación" por Administración.\n\nNo podrá realizar trámites hasta que su identidad y cargo sean validados.');
          return;
        }
        setFuncionarioData({ ...funcionarioData, nombre: data.nombre });
        fetchEquiposFuncionario(rutFormateado);
        setEtapa('SELECCION_TIPO');
      } else {
        setEtapa('REGISTRO');
      }
    } catch (err) {
      toast.error('Error al verificar identidad. Reintenta.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchEquiposFuncionario = async (rut) => {
    try {
      const resp = await fetch(`${apiBase}/api/publico/equipos-funcionario/${rut}`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setEquiposFuncionario(data);
      }
    } catch (err) {
      console.error('Error fetching equipments:', err);
    }
  };

  const handleRegisterFuncionario = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const resp = await fetch(`${apiBase}/api/publico/registrar-funcionario`, { credentials: 'include', 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(funcionarioData)
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success('Registro completado.\n\nSu cuenta se encuentra "Pendiente de Aprobación" por Administración. Podrá realizar solicitudes una vez que Jefatura valide su identidad y cargo.');
        window.location.reload();
      } else {
        toast.error(data.error || 'Error al registrar tus datos.');
      }
    } catch (err) {
      toast.error('Error de conexión al registrar funcionario.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitSolicitud = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let finalEquipoStr = solicitudData.nombre_equipo;
      const extra = {};

      if (solicitudData.tipo_solicitud === 'Nuevo Equipo') {
        finalEquipoStr = `[${solicitudData.tipo} - ${solicitudData.categoria}] ${solicitudData.nombre_equipo}`;
      } else {
        const eq = equiposFuncionario.find(e => e.id === solicitudData.equipo_id);
        finalEquipoStr = eq ? `${eq.modelo} (${eq.codigo})` : 'Equipo no especificado';
        extra.equipo_id = solicitudData.equipo_id;
        if (solicitudData.tipo_solicitud === 'Traslado') extra.destino = solicitudData.destino_traslado;
        if (solicitudData.tipo_solicitud === 'Préstamo') {
          extra.fecha_devolucion = solicitudData.fecha_devolucion;
          extra.destinatario_prestamo = solicitudData.destinatario_prestamo;
        }
      }

      const resp = await fetch(`${apiBase}/api/publico/solicitud`, { credentials: 'include', 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: funcionarioData.nombre,
          rut: funcionarioData.rut,
          tipo_solicitud: solicitudData.tipo_solicitud,
          motivo: solicitudData.motivo,
          equipo_solicitado: finalEquipoStr,
          datos_extra: extra
        })
      });
      if (resp.ok) {
        setSuccess(true);
      } else {
        toast.error('Hubo un error al enviar la solicitud.');
      }
    } catch (err) {
      toast.error('Error de conexión con el servidor municipal.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    window.location.reload();
  };


  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', background: 'var(--bg-main)' }}>
        <div className="chart-panel-premium animate-fade-in" style={{ maxWidth: '450px', textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '60px', color: 'var(--p-emerald)', marginBottom: '20px' }}>
            <i className="fa-solid fa-circle-check"></i>
          </div>
          <h2 style={{ color: 'var(--p-navy)', marginBottom: '10px' }}>Solicitud Registrada</h2>
          <p style={{ color: 'var(--p-slate)', lineHeight: '1.6' }}>
            Estimado/a <strong>{funcionarioData.nombre}</strong>, tu solicitud de nuevo equipo ha sido ingresada al sistema. 
            {solicitudData.tipo_solicitud === 'Extravío' || solicitudData.tipo_solicitud === 'Sustracción' 
              ? ' El Encargado de Inventario ha sido notificado directamente para el bloqueo del activo.'
              : ' Jefatura de tu Área revisará tu caso a la brevedad para su autorización.'
            }
          </p>
          <button className="btn-primary" style={{ marginTop: '30px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={resetForm}>Listo</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-main)', padding: '30px 16px' }}>
      {/* Botón flotante para cambiar de tema (Mismo de EquipoPublico) */}
      <button 
        onClick={toggleTheme}
        className="theme-toggle-floating"
        style={{
          position: 'fixed',
          top: '30px',
          right: '30px',
          background: 'var(--card-bg)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--glass-border)',
          color: theme === 'dark' ? 'var(--p-amber)' : 'var(--p-indigo)',
          padding: '10px 16px',
          borderRadius: '20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          zIndex: 1000,
          boxShadow: 'var(--shadow-lg)'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <i className={theme === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon'}></i>
        <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}</span>
      </button>

      <div style={{ maxWidth: '520px', margin: '0 auto', width: '100%' }}>

        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', background: 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))', borderRadius: '16px', marginBottom: '20px', boxShadow: '0 8px 20px rgba(99, 102, 241, 0.4)' }}>
            <i className="fa-solid fa-file-signature" style={{ color: 'white', fontSize: '24px' }}></i>
          </div>
          <h1 style={{ color: 'var(--p-navy)', fontSize: '28px', fontWeight: '800', margin: 0 }}>Portal de Solicitudes</h1>
        </div>

        {etapa === 'RUT' && (
          <form onSubmit={handleVerifyRut} className="chart-panel-premium animate-fade-in" style={{ padding: '30px' }}>
            <h3 style={{ color: 'var(--p-navy)', borderBottom: '1px solid var(--glass-border)', paddingBottom: '15px', marginBottom: '20px' }}>
              <i className="fa-solid fa-id-card" style={{ color: 'var(--p-blue)', marginRight: '10px' }}></i>
              Identificaci&oacute;n
            </h3>
            <div className="premium-field">
              <label><i className="fa-solid fa-id-card" style={{ color: 'var(--p-blue)' }}></i> Ingresa tu RUT</label>
              <input
                required
                type="text"
                autoFocus
                style={{ color: 'var(--p-navy)', background: 'var(--input-bg)', fontSize: '22px', textAlign: 'center', letterSpacing: '1px' }}
                placeholder="12.345.678-9"
                value={funcionarioData.rut}
                onChange={e => setFuncionarioData({ ...funcionarioData, rut: e.target.value })}
              />
            </div>
            <button type="submit" className="btn-primary" disabled={isSubmitting} style={{ width: '100%', height: '55px', marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {isSubmitting ? 'Verificando...' : 'Continuar'}
            </button>
            <p style={{ color: 'var(--p-slate)', fontSize: '11px', textAlign: 'center', marginTop: '20px' }}>
              Utiliza tu RUT para verificar si ya est&aacute;s en nuestro registro oficial de funcionarios.
            </p>
          </form>
        )}

        {etapa === 'REGISTRO' && (
          <form onSubmit={handleRegisterFuncionario} className="chart-panel-premium animate-fade-in" style={{ padding: '30px' }}>
            <h3 style={{ color: 'var(--p-navy)', borderBottom: '1px solid var(--glass-border)', paddingBottom: '15px', marginBottom: '20px' }}>
              <i className="fa-solid fa-user-plus" style={{ color: 'var(--p-amber)', marginRight: '10px' }}></i>
              Nuevo Funcionario
            </h3>
            <p style={{ color: 'var(--p-slate)', fontSize: '13px', marginBottom: '20px' }}>
              El RUT <strong>{funcionarioData.rut}</strong> no est&aacute; registrado. Por favor, completa tus datos para el registro municipal:
            </p>

            <div className="premium-field">
              <label><i className="fa-solid fa-user" style={{ color: 'var(--p-indigo)' }}></i> Nombre Completo</label>
              <input required type="text" style={{ color: 'var(--p-navy)', background: 'var(--input-bg)' }} value={funcionarioData.nombre} onChange={e => setFuncionarioData({ ...funcionarioData, nombre: e.target.value })} />
            </div>

            <div className="premium-field">
              <label><i className="fa-solid fa-envelope" style={{ color: 'var(--p-blue)' }}></i> Email Institucional</label>
              <input required type="email" style={{ color: 'var(--p-navy)', background: 'var(--input-bg)' }} placeholder="usuario@municipalidad.cl" value={funcionarioData.email} onChange={e => setFuncionarioData({ ...funcionarioData, email: e.target.value })} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
              <div className="premium-field">
                <label><i className="fa-solid fa-building" style={{ color: 'var(--p-emerald)' }}></i> Departamento</label>
                <input required type="text" style={{ color: 'var(--p-navy)', background: 'var(--input-bg)' }} placeholder="Ej: Salud" value={funcionarioData.departamento} onChange={e => setFuncionarioData({ ...funcionarioData, departamento: e.target.value })} />
              </div>
              <div className="premium-field">
                <label><i className="fa-solid fa-briefcase" style={{ color: 'var(--p-amber)' }}></i> Cargo</label>
                <input required type="text" style={{ color: 'var(--p-navy)', background: 'var(--input-bg)' }} placeholder="Ej: Administrativo" value={funcionarioData.cargo} onChange={e => setFuncionarioData({ ...funcionarioData, cargo: e.target.value })} />
              </div>
            </div>

            <button type="submit" className="btn-primary" disabled={isSubmitting} style={{ width: '100%', height: '55px', marginTop: '10px', background: 'var(--p-amber)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {isSubmitting ? 'Registrando...' : 'Finalizar Registro'}
            </button>
            <button type="button" className="btn-secondary" style={{ width: '100%', marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setEtapa('RUT')}>Cancelar</button>
          </form>
        )}

        {etapa === 'SELECCION_TIPO' && (
          <div className="chart-panel-premium animate-fade-in" style={{ padding: '30px' }}>
            <h3 style={{ color: 'var(--p-navy)', borderBottom: '1px solid var(--glass-border)', paddingBottom: '15px', marginBottom: '25px', textAlign: 'center' }}>
              ¿Qu&eacute; tr&aacute;mite deseas realizar?
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
              <button className="selection-card" style={{ borderLeft: '4px solid var(--p-indigo)' }} onClick={() => { setSolicitudData({ ...solicitudData, tipo_solicitud: 'Nuevo Equipo' }); setEtapa('SOLICITUD'); }}>
                <div className="selection-icon" style={{ background: 'var(--p-indigo)' }}>
                  <i className="fa-solid fa-plus"></i>
                </div>
                <div className="selection-info">
                  <div className="selection-title">Nuevo Equipo</div>
                  <div className="selection-desc">Solicitar un activo que no tienes asignado.</div>
                </div>
                <i className="fa-solid fa-chevron-right selection-arrow"></i>
              </button>

              <button className="selection-card" style={{ borderLeft: '4px solid var(--p-amber)' }} onClick={() => { setSolicitudData({ ...solicitudData, tipo_solicitud: 'Préstamo' }); setEtapa('SOLICITUD'); }}>
                <div className="selection-icon" style={{ background: 'var(--p-amber)' }}>
                  <i className="fa-solid fa-calendar-day"></i>
                </div>
                <div className="selection-info">
                  <div className="selection-title">Solicitar Préstamo</div>
                  <div className="selection-desc">Uso temporal de un equipo con fecha de retorno.</div>
                </div>
                <i className="fa-solid fa-chevron-right selection-arrow"></i>
              </button>

              <button className="selection-card" style={{ borderLeft: '4px solid var(--p-blue)' }} onClick={() => { setSolicitudData({ ...solicitudData, tipo_solicitud: 'Traslado' }); setEtapa('SOLICITUD'); }}>
                <div className="selection-icon" style={{ background: 'var(--p-blue)' }}>
                  <i className="fa-solid fa-truck-fast"></i>
                </div>
                <div className="selection-info">
                  <div className="selection-title">Informar Traslado</div>
                  <div className="selection-desc">Cambiar la ubicación física de un activo.</div>
                </div>
                <i className="fa-solid fa-chevron-right selection-arrow"></i>
              </button>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
                <button className="selection-card-mini alert" onClick={() => { setSolicitudData({ ...solicitudData, tipo_solicitud: 'Extravío' }); setEtapa('SOLICITUD'); }}>
                  <i className="fa-solid fa-magnifying-glass-minus"></i>
                  <span>Extravío</span>
                </button>
                <button className="selection-card-mini alert" onClick={() => { setSolicitudData({ ...solicitudData, tipo_solicitud: 'Sustracción' }); setEtapa('SOLICITUD'); }}>
                  <i className="fa-solid fa-mask"></i>
                  <span>Sustracción</span>
                </button>
              </div>
            </div>
            <button className="btn-secondary" style={{ width: '100%', marginTop: '25px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setEtapa('RUT')}>
              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }}></i> Volver
            </button>
          </div>
        )}

        {etapa === 'SOLICITUD' && (
          <form onSubmit={handleSubmitSolicitud} className="chart-panel-premium animate-fade-in" style={{ padding: '30px' }}>
            <div style={{ marginBottom: '20px', padding: '15px', background: 'var(--sidebar-accent)', borderRadius: '12px', borderLeft: '4px solid var(--p-indigo)', border: '1px solid var(--glass-border)', borderLeftWidth: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '10px', color: 'var(--p-slate)', textTransform: 'uppercase' }}>Tr&aacute;mite Seleccionado</span>
                  <h4 style={{ margin: 0, color: 'var(--p-navy)' }}>{solicitudData.tipo_solicitud}</h4>
                </div>
                <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setEtapa('SELECCION_TIPO')}>Cambiar</button>
              </div>
            </div>

            {solicitudData.tipo_solicitud === 'Nuevo Equipo' ? (
              <>
                <div className="premium-field">
                  <label><i className="fa-solid fa-computer" style={{ color: 'var(--p-indigo)' }}></i> Tipo de Equipamiento</label>
                  <select
                    required
                    className="select-premium"
                    style={{ width: '100%', height: '50px' }}
                    value={solicitudData.tipo}
                    onChange={e => {
                      const nuevoTipo = e.target.value;
                      const catsDisponibles = getCategoriasDisponibles(nuevoTipo);
                      setSolicitudData({
                        ...solicitudData,
                        tipo: nuevoTipo,
                        categoria: catsDisponibles.length > 0 ? catsDisponibles[0] : ''
                      });
                    }}
                  >
                    <option value="Activo">Activo (Computador, Servidor, etc.)</option>
                    <option value="Periferico">Perif&eacute;rico (Mouse, Teclado, Monitor, etc.)</option>
                  </select>
                </div>

                <div className="premium-field">
                    <label><i className="fa-solid fa-tag" style={{ color: 'var(--p-blue)' }}></i> Categoría Específica</label>
                  <select
                    required
                    className="select-premium"
                    style={{ width: '100%', height: '50px' }}
                    value={solicitudData.categoria}
                    onChange={e => setSolicitudData({ ...solicitudData, categoria: e.target.value })}
                  >
                    {getCategoriasDisponibles(solicitudData.tipo).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="premium-field">
                  <label><i className="fa-solid fa-pen-to-square" style={{ color: 'var(--p-emerald)' }}></i> Describe tu solicitud</label>
                  <input
                    required
                    type="text"
                    style={{ color: 'var(--p-navy)', background: 'var(--input-bg)' }}
                    placeholder="Ej: Notebook HP EliteBook, Monitor 24' Curvo..."
                    value={solicitudData.nombre_equipo}
                    onChange={e => setSolicitudData({ ...solicitudData, nombre_equipo: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <div className="premium-field">
                <label><i className="fa-solid fa-laptop" style={{ color: 'var(--p-blue)' }}></i> Seleccionar Equipo Afectado</label>
                {equiposFuncionario.length === 0 ? (
                  <div style={{ padding: '15px', background: 'rgba(244, 63, 94, 0.1)', borderRadius: '10px', color: 'var(--p-rose)', fontSize: '12px', border: '1px solid rgba(244, 63, 94, 0.2)' }}>
                    No tienes equipos asignados bajo tu RUT para realizar este trámite.
                  </div>
                ) : (
                  <select
                    required
                    className="select-premium"
                    style={{ width: '100%', height: '50px' }}
                    value={solicitudData.equipo_id}
                    onChange={e => setSolicitudData({ ...solicitudData, equipo_id: e.target.value })}
                  >
                    <option value="">Selecciona un activo...</option>
                    {equiposFuncionario.map(eq => (
                      <option key={eq.id} value={eq.id}>{eq.modelo} ({eq.codigo})</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {solicitudData.tipo_solicitud === 'Traslado' && (
              <div className="premium-field">
                <label><i className="fa-solid fa-building" style={{ color: 'var(--p-blue)' }}></i> Departamento / Oficina de Destino</label>
                <input
                  required
                  type="text"
                  style={{ color: 'var(--p-navy)', background: 'var(--input-bg)' }}
                  placeholder="Ej: Direcci&oacute;n de Obras - 2do Piso"
                  value={solicitudData.destino_traslado}
                  onChange={e => setSolicitudData({ ...solicitudData, destino_traslado: e.target.value })}
                />
              </div>
            )}

            {solicitudData.tipo_solicitud === 'Préstamo' && (
              <>
                <div className="premium-field animate-fade-in">
                  <label>
                    <i className="fa-solid fa-user-plus" style={{ marginRight: '8px', color: 'var(--p-amber)' }}></i>
                    FUNCIONARIO QUE RECIBIRÁ EL PRÉSTAMO
                  </label>
                  <select 
                    required
                    className="filter-select-premium"
                    style={{ width: '100%', marginBottom: '15px', color: 'var(--p-navy)', background: 'var(--input-bg)' }}
                    value={solicitudData.destinatario_prestamo || ''}
                    onChange={(e) => setSolicitudData({ ...solicitudData, destinatario_prestamo: e.target.value })}
                  >
                    <option value="">Seleccione al funcionario destinatario...</option>
                    {funcionariosDirectorio.map(f => (
                      <option key={f.id} value={f.nombre}>{f.nombre} ({f.departamento})</option>
                    ))}
                  </select>
                </div>
                <div className="premium-field">
                  <label><i className="fa-solid fa-calendar" style={{ color: 'var(--p-amber)' }}></i> Fecha Estimada de Devolución</label>
                  <input
                    required
                    type="date"
                    style={{ color: 'var(--p-navy)', background: 'var(--input-bg)' }}
                    value={solicitudData.fecha_devolucion}
                    onChange={e => setSolicitudData({ ...solicitudData, fecha_devolucion: e.target.value })}
                  />
                </div>
              </>
            )}

            <div className="premium-field">
              <label>
                <i className="fa-solid fa-message" style={{ color: 'var(--p-indigo)' }}></i> 
                {solicitudData.tipo_solicitud === 'Extravío' || solicitudData.tipo_solicitud === 'Sustracción' ? 'Relato de los Hechos' : 'Motivo o Justificación'}
              </label>
              <textarea
                required
                rows="5"
                style={{ color: 'var(--p-navy)', background: 'var(--input-bg)' }}
                placeholder={solicitudData.tipo_solicitud === 'Extravío' || solicitudData.tipo_solicitud === 'Sustracción' ? 'Describe d&oacute;nde y cu&aacute;ndo ocurri&oacute; el evento...' : 'Explica el motivo de tu solicitud...'}
                value={solicitudData.motivo}
                onChange={e => setSolicitudData({ ...solicitudData, motivo: e.target.value })}
              />
            </div>

            <button type="submit" className="btn-primary" disabled={isSubmitting || (solicitudData.tipo_solicitud !== 'Nuevo Equipo' && !solicitudData.equipo_id)} style={{ width: '100%', height: '55px', marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {isSubmitting ? 'Enviando...' : 'Enviar Solicitud Oficial'}
            </button>
            <button type="button" className="btn-secondary" style={{ width: '100%', marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setEtapa('SELECCION_TIPO')}>
              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }}></i> Volver
            </button>
          </form>
        )}

                <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--p-slate)', fontSize: '11px', letterSpacing: '1px', opacity: 0.5 }}>
                  MUNICIPALIDAD - GESTI&Oacute;N DE ACTIVOS 2026
                </div>
      </div>
    </div>
  );
};

export default SolicitudPublica;
